/**
 * 📁 FILE ICON - Microsoft Fluent UI icons from CDN
 */

import React from 'react';

const CDN_BASE =
  'https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/assets/item-types/48';
const VSCODE_ICONS_BASE =
  'https://raw.githubusercontent.com/vscode-icons/vscode-icons/master/icons';

/**
 * Get descriptive tooltip text for file extension
 */
const getFileTypeTooltip = ext => {
  const normalizedExt = (ext || '').toLowerCase().replace(/^\./, '');

  const tooltips = {
    // PDF
    pdf: 'PDF Document - pdf',

    // Word Documents
    doc: 'Word Document - doc',
    docx: 'Word Document - docx',
    odt: 'OpenDocument Text - odt',
    rtf: 'Rich Text Format - rtf',

    // Excel Spreadsheets
    xls: 'Excel Document - xls',
    xlsx: 'Excel Document - xlsx',
    csv: 'Comma Separated Values - csv',
    ods: 'OpenDocument Spreadsheet - ods',

    // PowerPoint
    ppt: 'PowerPoint Presentation - ppt',
    pptx: 'PowerPoint Presentation - pptx',
    odp: 'OpenDocument Presentation - odp',

    // Images
    jpg: 'JPEG Image - jpg',
    jpeg: 'JPEG Image - jpeg',
    png: 'PNG Image - png',
    gif: 'GIF Image - gif',
    bmp: 'Bitmap Image - bmp',
    webp: 'WebP Image - webp',
    tiff: 'TIFF Image - tiff',
    tif: 'TIFF Image - tif',
    svg: 'SVG Image - svg',
    heic: 'HEIC Image - heic',

    // Audio
    mp3: 'MP3 Audio - mp3',
    wav: 'WAV Audio - wav',
    m4a: 'M4A Audio - m4a',
    aac: 'AAC Audio - aac',
    ogg: 'OGG Audio - ogg',
    flac: 'FLAC Audio - flac',

    // Video
    mp4: 'MP4 Video - mp4',
    mov: 'QuickTime Video - mov',
    avi: 'AVI Video - avi',
    mkv: 'Matroska Video - mkv',
    webm: 'WebM Video - webm',

    // Text & Code
    txt: 'Text File - txt',
    md: 'Markdown File - md',
    json: 'JSON File - json',
    xml: 'XML File - xml',
    html: 'HTML File - html',
    css: 'CSS File - css',
    js: 'JavaScript File - js',
    ts: 'TypeScript File - ts',
    py: 'Python File - py',
    java: 'Java File - java',
    sql: 'SQL File - sql',

    // Archives
    zip: 'ZIP Archive - zip',
    rar: 'RAR Archive - rar',
    '7z': '7-Zip Archive - 7z',
    tar: 'TAR Archive - tar',
    gz: 'GZIP Archive - gz',

    // Email
    eml: 'Email Message - eml',
    msg: 'Outlook Message - msg',
  };

  return tooltips[normalizedExt] || `File - ${normalizedExt || 'unknown'}`;
};

const FILE_ICON_MAP = {
  // PDF - VS Code Icons (has "PDF" text on it)
  pdf: `${VSCODE_ICONS_BASE}/file_type_pdf.svg`,

  // Word Documents - VS Code Icons
  doc: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
  docx: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
  odt: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
  rtf: `${VSCODE_ICONS_BASE}/file_type_word.svg`,

  // Excel Spreadsheets - VS Code Icons
  xls: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
  xlsx: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
  csv: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
  ods: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,

  // PowerPoint - VS Code Icons
  ppt: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,
  pptx: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,
  odp: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,

  // Images (keep emoji for now, or use generic icon)
  jpg: '🖼️',
  jpeg: '🖼️',
  png: '🖼️',
  gif: '🖼️',
  bmp: '🖼️',
  webp: '🖼️',
  tiff: '🖼️',
  tif: '🖼️',
  svg: '🎨',
  heic: '🖼️',

  // Audio
  mp3: '🎧',
  wav: '🎧',
  m4a: '🎧',
  aac: '🎧',
  ogg: '🎵',
  flac: '🎧',

  // Video
  mp4: '🎥',
  mov: '🎥',
  avi: '🎬',
  mkv: '🎬',
  webm: '🎥',

  // Text & Code
  txt: '📘',
  md: '📖',
  json: '📋',
  xml: '📋',
  html: '🌐',
  css: '🎨',
  js: '⚡',
  ts: '💎',
  py: '🐍',
  java: '☕',
  sql: '🗃️',

  // Archives
  zip: '📦',
  rar: '📦',
  '7z': '📦',
  tar: '📦',
  gz: '📦',

  // Email
  eml: '✉️',
  msg: '✉️',
};

/**
 * FileIcon Component
 * @param {Object} props
 * @param {string} props.ext - File extension
 * @param {string} props.className - Additional CSS classes
 */
export default function FileIcon({ ext, className = '' }) {
  const [imageError, setImageError] = React.useState(false);

  const normalizedExt = (ext || '').toLowerCase().replace(/^\./, '');
  const icon = FILE_ICON_MAP[normalizedExt] || '📁';
  const tooltip = getFileTypeTooltip(normalizedExt);

  // Check if icon is a URL (CDN) or emoji
  const isCDNIcon = typeof icon === 'string' && icon.startsWith('http');

  // If CDN icon failed to load, fallback to emoji
  if (isCDNIcon && !imageError) {
    return (
      <img
        src={icon}
        alt={`${normalizedExt} file`}
        title={tooltip}
        className={`file-icon ${className}`}
        style={{ width: '28px', height: '28px', objectFit: 'contain', cursor: 'help' }}
        onError={() => setImageError(true)}
      />
    );
  }

  // Return emoji for non-CDN icons or fallback
  return (
    <span
      className={`file-icon ${className}`}
      role="img"
      aria-label={`${normalizedExt} file`}
      title={tooltip}
      style={{ cursor: 'help' }}
    >
      {imageError ? '📁' : icon}
    </span>
  );
}

export { FileIcon };
